// src/services/repeatCheck.ts
// ---------------------------
// Possible-repeat flag (db-210) — DISPLAY helpers only. Pure, no I/O.
//
// The matching RULES are NOT here: they live only in the database function
// request_repeat_matches() (one source of truth, per the db-193/194 drift lesson). This file
// turns the rows that function returns into what the request screen shows: per fee/device,
// the earlier requests, whether any match is strong, and the total already approved.

import { FEE_TYPE_VALUES } from "../utils/enums";

export const REPEAT_CHECK_DEVICE = "Device";
/** Every item type the check can cover: the 10 fee types + devices. */
export const REPEAT_CHECK_ALL_TYPES: string[] = [...FEE_TYPE_VALUES, REPEAT_CHECK_DEVICE];
export const DEFAULT_REPEAT_MONTHS = 12;
/** portal_config keys (System Setup → Access). */
export const REPEAT_MONTHS_KEY = "repeat_check_months";
export const REPEAT_TYPES_KEY = "repeat_check_types";

/** The subset of a request_repeat_matches() row this file reads. */
export interface RepeatMatchLike {
  item_kind: string;
  item_id: string;
  child_index: number;
  item_type: string;
  match_request_id: number;
  match_ref_no: string;
  match_created_at: string;
  match_item_id: string;
  match_desc: string | null;
  match_amount: number | string | null;
  match_decision: string;
  strong: boolean;
}

export interface RepeatEarlier {
  requestId: number;
  refNo: string;
  createdAt: string;
  desc: string | null;
  amount: number;
  decision: string;
  strong: boolean;
}

export interface ItemRepeat {
  itemId: string;
  itemKind: string; // 'fee' | 'device'
  itemType: string; // fee type, or 'Device'
  childIndex: number;
  strong: boolean; // any earlier match shares the Client Reference
  earlier: RepeatEarlier[]; // newest first
  approvedTotal: number; // sum of the earlier APPROVED amounts
}

export type RepeatsByItem = Record<string, ItemRepeat>;

function amountOf(v: number | string | null | undefined): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Group the function's rows by the item on THIS request (fee id / device id). */
export function groupRepeatMatches(rows: RepeatMatchLike[] | null | undefined): RepeatsByItem {
  const out: RepeatsByItem = {};
  const seen = new Set<string>();
  for (const r of rows || []) {
    if (!r?.item_id) continue;
    // One earlier item can only count once per item on this request.
    const key = `${r.item_id}|${r.match_item_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    let it = out[r.item_id];
    if (!it) {
      it = {
        itemId: r.item_id,
        itemKind: r.item_kind,
        itemType: r.item_type,
        childIndex: r.child_index,
        strong: false,
        earlier: [],
        approvedTotal: 0,
      };
      out[r.item_id] = it;
    }
    const amount = amountOf(r.match_amount);
    it.earlier.push({
      requestId: r.match_request_id,
      refNo: r.match_ref_no,
      createdAt: r.match_created_at,
      desc: r.match_desc,
      amount,
      decision: r.match_decision,
      strong: !!r.strong,
    });
    if (r.strong) it.strong = true;
    if (r.match_decision === "Approved") it.approvedTotal += amount;
  }
  for (const it of Object.values(out)) {
    it.earlier.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    it.approvedTotal = Math.round(it.approvedTotal * 100) / 100;
  }
  return out;
}

export interface RepeatSummary {
  count: number; // flagged items on this request
  strong: boolean; // any of them strong
  childIndexes: number[]; // which children (0-based), ascending
}

/** Request-level summary for the Summary card and the Confirm & Send gate. */
export function repeatSummary(byItem: RepeatsByItem | null | undefined): RepeatSummary {
  const items = Object.values(byItem || {});
  const kids = Array.from(new Set(items.map((i) => i.childIndex))).sort((a, b) => a - b);
  return { count: items.length, strong: items.some((i) => i.strong), childIndexes: kids };
}

/** The months setting as a whole number ≥ 1 (default 12). */
export function normaliseRepeatMonths(v: unknown): number {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) && n >= 1 ? n : DEFAULT_REPEAT_MONTHS;
}

/**
 * The types setting as the function's p_types: null when every type is on (= check all),
 * otherwise the known types that are ticked. A missing / unreadable setting = all on.
 */
export function repeatTypesParam(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  const on = REPEAT_CHECK_ALL_TYPES.filter((t) => v.includes(t));
  return on.length === REPEAT_CHECK_ALL_TYPES.length ? null : on;
}

/** One flagged item, as listed in the Repeat check pop-up at Confirm & Send. */
export interface FlaggedConfirmItem {
  key: string; // fee / device id
  childIndex: number; // 0-based
  label: string; // fee type, or "Device"
  desc: string;
  amount: number;
  decision: string; // the decision being confirmed now
  strong: boolean;
  earlier: { refNo: string; decision: string; createdAt: string }[];
}

interface ChildLike {
  fees?: {
    id?: string;
    feeType?: string;
    feeDesc?: string;
    feeAmount?: number | string;
    approvalStatus?: string;
  }[];
  laptop?: {
    id?: string;
    deviceType?: string;
    estimatedCost?: number | string;
    approvalStatus?: string;
  } | null;
}

/**
 * The flagged fees/devices on a request, in child order, with the decision about to be
 * confirmed. Empty = no Repeat check note needed. Pure.
 */
export function flaggedItemsForConfirm(
  children: ChildLike[] | null | undefined,
  byItem: RepeatsByItem | null | undefined
): FlaggedConfirmItem[] {
  if (!byItem) return [];
  const out: FlaggedConfirmItem[] = [];
  (children || []).forEach(function (c, ci) {
    for (const f of c?.fees || []) {
      const r = f?.id ? byItem[f.id] : undefined;
      if (!r) continue;
      out.push({
        key: f.id as string,
        childIndex: ci,
        label: f.feeType || r.itemType,
        desc: f.feeDesc || "",
        amount: amountOf(f.feeAmount),
        decision: f.approvalStatus || "Pending",
        strong: r.strong,
        earlier: r.earlier.map((e) => ({
          refNo: e.refNo,
          decision: e.decision,
          createdAt: e.createdAt,
        })),
      });
    }
    const l = c?.laptop;
    const r = l?.id ? byItem[l.id] : undefined;
    if (l && r) {
      out.push({
        key: l.id as string,
        childIndex: ci,
        label: REPEAT_CHECK_DEVICE,
        desc: l.deviceType || "",
        amount: amountOf(l.estimatedCost),
        decision: l.approvalStatus || "Pending",
        strong: r.strong,
        earlier: r.earlier.map((e) => ({
          refNo: e.refNo,
          decision: e.decision,
          createdAt: e.createdAt,
        })),
      });
    }
  });
  return out;
}

/** The text saved as the internal note (kind "repeat_check"). */
export const REPEAT_NOTE_KIND = "repeat_check";
export const REPEAT_NOTE_PREFIX = "Repeat check: ";

export function repeatNoteText(note: string): string {
  return `${REPEAT_NOTE_PREFIX}${String(note || "").trim()}`;
}

/** Latest repeat_check internal note (any shape with kind/text/author/created_at). */
export function latestRepeatReview(
  notes: { kind?: string; text?: string; author?: string; created_at?: string }[] | null
): { author: string; text: string; date: string } | null {
  const mine = (notes || []).filter((n) => n?.kind === REPEAT_NOTE_KIND && n.text);
  if (mine.length === 0) return null;
  const last = mine.reduce((a, b) =>
    Date.parse(b.created_at || "") > Date.parse(a.created_at || "") ? b : a
  );
  const text = String(last.text);
  return {
    author: last.author || "",
    text: text.startsWith(REPEAT_NOTE_PREFIX) ? text.slice(REPEAT_NOTE_PREFIX.length) : text,
    date: last.created_at || "",
  };
}

// ── Phase B: list chips + the All Requests "Possible repeats" filter ─────────────────────────

/**
 * Phase A (the required Repeat check note) went live on 24 Oct 2026. Requests confirmed
 * before then could not have a note, so they never count as "Not reviewed".
 */
export const REPEAT_CHECK_LIVE_FROM = "2026-10-24T00:00:00+10:00";

export interface RepeatFlag {
  flaggedItems: number;
  strong: boolean;
  reviewed: boolean;
  /** db-212: a match was created BEFORE this request. False = only later twins. */
  hasEarlier: boolean;
}
export type RepeatFlagsByRequest = Record<string, RepeatFlag>;

/** request_repeat_flags() rows → a map keyed by request id. */
export function toRepeatFlagMap(
  rows:
    | {
        request_id: number;
        flagged_items: number;
        strong: boolean;
        reviewed: boolean;
        has_earlier?: boolean | null;
      }[]
    | null
    | undefined
): RepeatFlagsByRequest {
  const out: RepeatFlagsByRequest = {};
  for (const r of rows || []) {
    if (!r || r.request_id == null) continue;
    out[String(r.request_id)] = {
      flaggedItems: Number(r.flagged_items) || 0,
      strong: !!r.strong,
      reviewed: !!r.reviewed,
      // Missing (function not yet db-212) → true, so nothing is hidden from "Not reviewed".
      hasEarlier: r.has_earlier !== false,
    };
  }
  return out;
}

/**
 * Where a request stands on the repeat check:
 *   none          — not flagged
 *   reviewed      — flagged, Repeat check note saved
 *   to_review     — flagged, not confirmed yet (the note is asked for at Confirm & Send)
 *   not_reviewed  — flagged, confirmed on/after go-live with no note (needs a look)
 *   before_check  — flagged, confirmed before go-live (no note was possible)
 *   later_match   — flagged, confirmed with no note, but every match was created AFTER this
 *                   request (its twin came later, so no note could be asked for) — db-212
 */
export type RepeatReviewState =
  | "none"
  | "reviewed"
  | "to_review"
  | "not_reviewed"
  | "before_check"
  | "later_match";

export function repeatReviewState(
  flag: RepeatFlag | null | undefined,
  approvalsConfirmedAt: string | null | undefined
): RepeatReviewState {
  if (!flag) return "none";
  if (flag.reviewed) return "reviewed";
  if (!approvalsConfirmedAt) return "to_review";
  if (Date.parse(approvalsConfirmedAt) < Date.parse(REPEAT_CHECK_LIVE_FROM)) return "before_check";
  return flag.hasEarlier === false ? "later_match" : "not_reviewed";
}

/** All Requests "Possible repeats" filter values. */
export const REPEAT_FILTERS = [
  { value: "All", label: "Repeats: any request" },
  { value: "flagged", label: "All possible repeats" },
  { value: "not_reviewed", label: "Not reviewed" },
  { value: "reviewed", label: "Reviewed" },
] as const;

export function passesRepeatFilter(filter: string, state: RepeatReviewState): boolean {
  if (filter === "flagged") return state !== "none";
  if (filter === "not_reviewed") return state === "not_reviewed";
  if (filter === "reviewed") return state === "reviewed";
  return true;
}
